import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { OrganizationPathParamsSchema } from "@common/schemas/organization-service.schema";
import {
    ReportSettingsTemplateCreateSchema,
    ReportSettingsTemplateIdPathParamsSchema,
    ReportSettingsTemplateListResponseSchema,
    ReportSettingsTemplateSchema,
    ReportSettingsTemplateSetDefaultSchema,
    ReportSettingsTemplateUpdateSchema,
} from "@common/schemas/domains/reportSettings/report-settings-template.schema";
import {
    ReportSettingsTemplateApplySchema,
    ReportSettingsTemplateTargetsResponseSchema,
} from "@common/schemas/domains/reportSettings/report-settings-template-application.schema";
import {
    saveTemplateReportSettingsBodySchema,
    templateReportSettingsResponseSchema,
} from "@common/schemas/domains/reportSettings/report-settings-template-settings.schema";

const c = initContract();

export const reportSettingsTemplateContract = c.router(
    {
        listReportSettingsTemplates: {
            method: "GET",
            path: "/organizations/:organizationId",
            pathParams: OrganizationPathParamsSchema,
            responses: {
                200: ReportSettingsTemplateListResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createReportSettingsTemplate: {
            method: "POST",
            path: "/organizations/:organizationId",
            pathParams: OrganizationPathParamsSchema,
            body: ReportSettingsTemplateCreateSchema,
            responses: {
                201: ReportSettingsTemplateSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        updateReportSettingsTemplate: {
            method: "PATCH",
            path: "/organizations/:organizationId/:reportSettingsTemplateId",
            pathParams: OrganizationPathParamsSchema.merge(
                ReportSettingsTemplateIdPathParamsSchema,
            ),
            body: ReportSettingsTemplateUpdateSchema,
            responses: {
                200: ReportSettingsTemplateSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        archiveReportSettingsTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:reportSettingsTemplateId/archive",
            pathParams: OrganizationPathParamsSchema.merge(
                ReportSettingsTemplateIdPathParamsSchema,
            ),
            body: c.noBody(),
            responses: {
                200: ReportSettingsTemplateSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        unarchiveReportSettingsTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:reportSettingsTemplateId/unarchive",
            pathParams: OrganizationPathParamsSchema.merge(
                ReportSettingsTemplateIdPathParamsSchema,
            ),
            body: c.noBody(),
            responses: {
                200: ReportSettingsTemplateSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        deleteReportSettingsTemplate: {
            method: "DELETE",
            path: "/organizations/:organizationId/:reportSettingsTemplateId",
            pathParams: OrganizationPathParamsSchema.merge(
                ReportSettingsTemplateIdPathParamsSchema,
            ),
            responses: {
                200: c.noBody(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        setDefaultReportSettingsTemplate: {
            method: "PUT",
            path: "/organizations/:organizationId/default",
            pathParams: OrganizationPathParamsSchema,
            body: ReportSettingsTemplateSetDefaultSchema,
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        getReportSettingsTemplateTargets: {
            method: "GET",
            path: "/organizations/:organizationId/targets",
            pathParams: OrganizationPathParamsSchema,
            responses: {
                200: ReportSettingsTemplateTargetsResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        applyReportSettingsTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/applications",
            pathParams: OrganizationPathParamsSchema,
            body: ReportSettingsTemplateApplySchema,
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        getReportSettingsTemplateSettings: {
            method: "GET",
            path: "/organizations/:organizationId/:reportSettingsTemplateId/settings",
            pathParams: OrganizationPathParamsSchema.merge(
                ReportSettingsTemplateIdPathParamsSchema,
            ),
            responses: {
                200: templateReportSettingsResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateReportSettingsTemplateSettings: {
            method: "PATCH",
            path: "/organizations/:organizationId/:reportSettingsTemplateId/settings",
            pathParams: OrganizationPathParamsSchema.merge(
                ReportSettingsTemplateIdPathParamsSchema,
            ),
            body: saveTemplateReportSettingsBodySchema,
            responses: {
                200: z.void(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/report-settings-templates",
    },
);
